import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { signIn, signInWithMicrosoft } from '@/lib/supabase';
import { toast } from 'sonner';
import { useDarkMode } from '@/hooks/useDarkMode';
import { Lock } from 'lucide-react';

const DOMAINS = ['intercomp.com.mt', 'intercomp.pro'] as const;

const BOOT_LINES = [
  'AOI Hub v2.0 — Secure Terminal',
  'Initializing authentication modules...',
  'Loading cryptographic libraries... [OK]',
  'Verifying identity providers........[OK]',
  'Establishing secure channel...........[OK]',
  'System ready.',
];

// ── Microsoft logo ─────────────────────────────────────────────────────────────

function MicrosoftLogo({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

// ── Perspective grid canvas ────────────────────────────────────────────────────

function TerminalCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let t = 0;

    function resize() {
      canvas!.width = window.innerWidth;
      canvas!.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    function draw() {
      const W = canvas!.width;
      const H = canvas!.height;
      ctx.clearRect(0, 0, W, H);

      // Background gradient
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#050508');
      bg.addColorStop(1, '#020204');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      const vy = H * 0.52;
      const vx = W / 2;
      const COLS = 28;
      const ROWS = 22;

      t += 0.006;

      // ── Vertical lines converging to vanish point ──────────────────────────
      for (let i = 0; i <= COLS; i++) {
        const xBase = (i / COLS) * W;
        const distFromCenter = Math.abs(i / COLS - 0.5) * 2;
        const alpha = Math.max(0, (1 - distFromCenter) * 0.35);
        ctx.strokeStyle = `rgba(74, 222, 128, ${alpha})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        // Near the horizon, lines converge very close to vx
        ctx.moveTo(vx + (xBase - vx) * 0.02, vy);
        ctx.lineTo(xBase, H);
        ctx.stroke();
      }

      // ── Horizontal lines scrolling toward viewer ───────────────────────────
      for (let i = 0; i < ROWS; i++) {
        const progress = ((i / ROWS + t) % 1);
        const y = vy + Math.pow(progress, 1.8) * (H - vy);
        ctx.strokeStyle = `rgba(74, 222, 128, ${progress * 0.4})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }

      // ── Bright accent lines (faster, more vivid) ──────────────────────────
      for (let i = 0; i < 4; i++) {
        const progress = ((i / 4 + t * 0.7) % 1);
        const y = vy + Math.pow(progress, 1.8) * (H - vy);
        const alpha = progress * 0.7;
        ctx.strokeStyle = `rgba(74, 222, 128, ${alpha})`;
        ctx.lineWidth = progress * 2;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }

      // ── Horizon glow ───────────────────────────────────────────────────────
      const glow = ctx.createLinearGradient(0, vy - 60, 0, vy + 30);
      glow.addColorStop(0, 'rgba(74, 222, 128, 0)');
      glow.addColorStop(0.5, 'rgba(74, 222, 128, 0.06)');
      glow.addColorStop(1, 'rgba(74, 222, 128, 0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, vy - 60, W, 90);

      rafRef.current = requestAnimationFrame(draw);
    }

    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}

// ── Scanlines ──────────────────────────────────────────────────────────────────

function Scanlines() {
  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{
        zIndex: 20,
        backgroundImage:
          'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.12) 2px, rgba(0,0,0,0.12) 4px)',
      }}
    />
  );
}

// ── Terminal login panel ───────────────────────────────────────────────────────

const MONO = "'JetBrains Mono', 'Courier New', monospace";

function TerminalLogin({
  username,
  setUsername,
  password,
  setPassword,
  loading,
  onSubmit,
  onMicrosoft,
  oauthLoading,
  passwordFailed,
}: {
  username: string;
  setUsername: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onMicrosoft: () => void;
  oauthLoading: boolean;
  passwordFailed: boolean;
}) {
  const [visibleLines, setVisibleLines] = useState(0);
  const [bootDone, setBootDone] = useState(false);
  const idRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let idx = 0;
    const id = setInterval(() => {
      idx++;
      setVisibleLines(idx);
      if (idx >= BOOT_LINES.length) {
        clearInterval(id);
        setTimeout(() => {
          setBootDone(true);
          setTimeout(() => idRef.current?.focus(), 50);
        }, 350);
      }
    }, 260);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="relative flex flex-col items-center justify-center min-h-screen px-4"
      style={{ zIndex: 10, fontFamily: MONO }}
    >
      {/* Window */}
      <div
        className="w-full max-w-md"
        style={{
          background: 'rgba(3, 3, 5, 0.93)',
          border: '1px solid rgba(74, 222, 128, 0.25)',
          borderRadius: 8,
          backdropFilter: 'blur(12px)',
          boxShadow:
            '0 0 0 1px rgba(74,222,128,0.05), 0 0 40px rgba(74,222,128,0.08), 0 20px 60px rgba(0,0,0,0.6)',
        }}
      >
        {/* Title bar */}
        <div
          className="flex items-center gap-2 px-4 py-2.5"
          style={{ borderBottom: '1px solid rgba(74,222,128,0.12)' }}
        >
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ff5f56', display: 'inline-block', flexShrink: 0 }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ffbd2e', display: 'inline-block', flexShrink: 0 }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#27c93f', display: 'inline-block', flexShrink: 0 }} />
          <span className="mx-auto" style={{ color: 'rgba(74,222,128,0.35)', fontSize: 11 }}>
            ssh auth@hub.intercomp.mt
          </span>
        </div>

        {/* Body */}
        <div className="p-6" style={{ color: '#4ade80' }}>
          {/* Boot log */}
          <div className="mb-5 space-y-0.5" style={{ minHeight: `${BOOT_LINES.length * 1.6}rem` }}>
            {BOOT_LINES.slice(0, visibleLines).map((line, i) => {
              const isLast = i === BOOT_LINES.length - 1;
              const isDone = i < visibleLines - 1;
              if (line.includes('[OK]')) {
                const base = line.replace(' [OK]', '').replace('...[OK]', '...').replace('........[OK]', '........').replace('...........[OK]', '...........');
                const suffix = line.includes('[OK]') ? ' [OK]' : '';
                return (
                  <div key={i} style={{ fontSize: 12, opacity: isDone ? 0.45 : 1 }}>
                    <span style={{ color: 'rgba(74,222,128,0.7)' }}>{base}</span>
                    <span style={{ color: '#4ade80' }}>{suffix}</span>
                  </div>
                );
              }
              return (
                <div
                  key={i}
                  style={{
                    fontSize: 12,
                    opacity: isDone ? 0.45 : 1,
                    color: isLast ? '#4ade80' : 'rgba(74,222,128,0.7)',
                    fontWeight: isLast ? 700 : 400,
                  }}
                >
                  {line}
                </div>
              );
            })}
          </div>

          {/* Auth form */}
          <div
            style={{
              opacity: bootDone ? 1 : 0,
              transform: bootDone ? 'translateY(0)' : 'translateY(6px)',
              transition: 'opacity 0.4s, transform 0.4s',
            }}
          >
            <form onSubmit={onSubmit}>
              {/* ID field */}
              <div
                className="flex items-center gap-3 mb-3 px-3 py-2.5"
                style={{
                  border: '1px solid rgba(74,222,128,0.15)',
                  borderRadius: 4,
                  background: 'rgba(74,222,128,0.03)',
                  transition: 'border-color 0.2s',
                }}
                onFocusCapture={e => (e.currentTarget.style.borderColor = 'rgba(74,222,128,0.4)')}
                onBlurCapture={e => (e.currentTarget.style.borderColor = 'rgba(74,222,128,0.15)')}
              >
                <span style={{ color: 'rgba(74,222,128,0.5)', fontSize: 13, userSelect: 'none' }}>$</span>
                <span style={{ color: 'rgba(74,222,128,0.4)', fontSize: 11, minWidth: '2.5rem' }}>ID</span>
                <input
                  ref={idRef}
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  placeholder="your.name"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: '#4ade80',
                    fontFamily: MONO,
                    fontSize: 13,
                    caretColor: '#4ade80',
                  }}
                />
              </div>

              {/* Password field */}
              <div
                className="flex items-center gap-3 mb-4 px-3 py-2.5"
                style={{
                  border: '1px solid rgba(74,222,128,0.15)',
                  borderRadius: 4,
                  background: 'rgba(74,222,128,0.03)',
                  transition: 'border-color 0.2s',
                }}
                onFocusCapture={e => (e.currentTarget.style.borderColor = 'rgba(74,222,128,0.4)')}
                onBlurCapture={e => (e.currentTarget.style.borderColor = 'rgba(74,222,128,0.15)')}
              >
                <span style={{ color: 'rgba(74,222,128,0.5)', fontSize: 13, userSelect: 'none' }}>$</span>
                <span style={{ color: 'rgba(74,222,128,0.4)', fontSize: 11, minWidth: '2.5rem' }}>PASS</span>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: '#4ade80',
                    fontFamily: MONO,
                    fontSize: 13,
                    caretColor: '#4ade80',
                  }}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2.5 text-left"
                style={{
                  background: loading ? 'rgba(74,222,128,0.06)' : 'transparent',
                  border: '1px solid rgba(74,222,128,0.25)',
                  borderRadius: 4,
                  color: '#4ade80',
                  fontFamily: MONO,
                  fontSize: 13,
                  cursor: loading ? 'wait' : 'pointer',
                  transition: 'background 0.15s, border-color 0.15s',
                }}
                onMouseEnter={e => {
                  if (!loading) {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(74,222,128,0.07)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(74,222,128,0.5)';
                  }
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(74,222,128,0.25)';
                }}
              >
                <span style={{ color: 'rgba(74,222,128,0.45)' }}>{'$ '}</span>
                {loading ? 'authenticating...' : './authenticate --secure'}
              </button>
            </form>

            {passwordFailed && (
              <div
                className="mt-3 px-3 py-2"
                style={{
                  border: '1px solid rgba(251,191,36,0.3)',
                  borderRadius: 4,
                  background: 'rgba(251,191,36,0.06)',
                  color: '#fbbf24',
                  fontSize: 11,
                  lineHeight: 1.5,
                }}
              >
                auth rejected — intercomp accounts now use microsoft sso
              </div>
            )}

            {/* SSO divider */}
            <div className="flex items-center gap-3 my-4">
              <span style={{ flex: 1, height: 1, background: 'rgba(74,222,128,0.12)' }} />
              <span style={{ color: 'rgba(74,222,128,0.3)', fontSize: 10 }}>OR</span>
              <span style={{ flex: 1, height: 1, background: 'rgba(74,222,128,0.12)' }} />
            </div>

            <button
              type="button"
              onClick={onMicrosoft}
              disabled={oauthLoading}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left"
              style={{
                background: oauthLoading ? 'rgba(74,222,128,0.06)' : 'transparent',
                border: '1px solid rgba(74,222,128,0.25)',
                borderRadius: 4,
                color: '#4ade80',
                fontFamily: MONO,
                fontSize: 13,
                cursor: oauthLoading ? 'wait' : 'pointer',
                transition: 'background 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => {
                if (!oauthLoading) {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(74,222,128,0.07)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(74,222,128,0.5)';
                }
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(74,222,128,0.25)';
              }}
            >
              <MicrosoftLogo size={14} />
              {oauthLoading ? 'redirecting...' : './sso --microsoft'}
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <p
        className="mt-4"
        style={{ color: 'rgba(74,222,128,0.2)', fontSize: 11, fontFamily: MONO }}
      >
        AOI Hub · intercomp.mt · {new Date().getFullYear()}
      </p>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function LoginPage() {
  useDarkMode();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [passwordFailed, setPasswordFailed] = useState(false);
  // ?password deep-links straight to the form, so break-glass instructions can
  // be a URL in the vault rather than a described click path.
  const [showPassword, setShowPassword] = useState(
    () => new URLSearchParams(window.location.search).has('password'),
  );
  const passwordIdRef = useRef<HTMLInputElement>(null);

  function revealPassword() {
    setShowPassword(true);
    setTimeout(() => passwordIdRef.current?.focus(), 50);
  }
  const [terminalMode, setTerminalMode] = useState(
    () => localStorage.getItem('login-theme') === 'terminal',
  );

  // Secret toggle: triple-click logo OR Alt+T
  const logoClickCount = useRef(0);
  const logoClickTimer = useRef<ReturnType<typeof setTimeout>>();

  function handleLogoClick() {
    logoClickCount.current++;
    clearTimeout(logoClickTimer.current);
    logoClickTimer.current = setTimeout(() => {
      logoClickCount.current = 0;
    }, 600);
    if (logoClickCount.current >= 3) {
      logoClickCount.current = 0;
      toggleTerminal();
    }
  }

  function toggleTerminal() {
    setTerminalMode(v => {
      const next = !v;
      localStorage.setItem('login-theme', next ? 'terminal' : 'default');
      return next;
    });
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.altKey || e.metaKey) && e.key === 't') {
        e.preventDefault();
        toggleTerminal();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Inject JetBrains Mono when terminal mode is active
  useEffect(() => {
    if (!terminalMode) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap';
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, [terminalMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setPasswordFailed(false);
    try {
      const isFullEmail = username.includes('@');
      const results = isFullEmail
        ? [await signIn(username, password)]
        : await Promise.all(DOMAINS.map(domain => signIn(`${username}@${domain}`, password)));
      const success = results.find(r => !r.error);
      if (success) return;
      // Supabase deliberately returns the same "Invalid login credentials" whether
      // the password is wrong or the account no longer has one, so we can't tell
      // the two apart - offer the SSO handoff either way rather than guessing.
      setPasswordFailed(true);
    } catch {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // On success the browser leaves for Microsoft, so loading is only ever cleared
  // on failure - keeping it set avoids a flash of the enabled button mid-redirect.
  const handleMicrosoft = async () => {
    setOauthLoading(true);
    // Only a full address is a usable hint - with both intercomp domains live a
    // bare username can't be resolved to one, so let Microsoft ask instead.
    const hint = username.includes('@') ? username.trim() : undefined;
    const { error } = await signInWithMicrosoft(hint);
    if (error) {
      toast.error(error.message);
      setOauthLoading(false);
    }
  };

  // ── Terminal theme ─────────────────────────────────────────────────────────
  if (terminalMode) {
    return (
      <div className="relative min-h-screen overflow-hidden" style={{ background: '#050508' }}>
        <TerminalCanvas />
        <Scanlines />
        <TerminalLogin
          username={username}
          setUsername={setUsername}
          password={password}
          setPassword={setPassword}
          loading={loading}
          onSubmit={handleSubmit}
          onMicrosoft={handleMicrosoft}
          oauthLoading={oauthLoading}
          passwordFailed={passwordFailed}
        />
        {/* Invisible logo click target in corner */}
        <button
          onClick={handleLogoClick}
          className="fixed top-4 left-4 opacity-0 pointer-events-auto"
          style={{ width: 40, height: 40 }}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
    );
  }

  // ── Default theme ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4 relative">
      {/* Dot grid — light */}
      <div
        className="fixed inset-0 pointer-events-none dark:hidden"
        style={{ backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.18) 1px, transparent 1px)', backgroundSize: '22px 22px', zIndex: 0 }}
      />
      {/* Dot grid — dark */}
      <div
        className="fixed inset-0 pointer-events-none hidden dark:block"
        style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '22px 22px', zIndex: 0 }}
      />

      <div className="w-full max-w-sm relative" style={{ zIndex: 1 }}>
        {/* Logo — triple-click toggles terminal mode */}
        <div className="flex justify-center mb-2">
          <img
            src="/logo-horizontal-noshadow.png"
            alt="AOI Hub"
            className="h-14 w-auto dark:hidden cursor-default select-none"
            onClick={handleLogoClick}
            draggable={false}
          />
          <img
            src="/logo-dark.png"
            alt="AOI Hub"
            className="h-14 w-auto hidden dark:block cursor-default select-none"
            onClick={handleLogoClick}
            draggable={false}
          />
        </div>

        {/* Tagline */}
        <p className="text-center text-xs text-gray-400 dark:text-gray-600 tracking-widest uppercase mb-6">
          PC Builder Management
        </p>

        {/* Card */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 shadow-sm dark:shadow-2xl">
          <h1 className="text-gray-900 dark:text-white text-xl font-semibold mb-1">Sign in</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">Access the AOI Hub</p>

          <Button
            type="button"
            className="w-full gap-2"
            onClick={handleMicrosoft}
            disabled={oauthLoading}
          >
            <MicrosoftLogo />
            {oauthLoading ? 'Redirecting...' : 'Sign in with Microsoft'}
          </Button>

          {/* Password sign-in is for break-glass and the shared station logins
              that have no Entra identity. Deliberately quiet but not hidden -
              during a Microsoft outage someone other than an admin may need to
              find it, so an undocumented gesture would be its own outage. */}
          {!showPassword ? (
            <button
              type="button"
              onClick={revealPassword}
              className="mt-4 w-full text-center text-xs text-gray-400 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-400 hover:underline"
            >
              Use a password instead
            </button>
          ) : (
            <>
              <div className="flex items-center gap-3 my-5">
                <span className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
                <span className="text-xs text-gray-400 dark:text-gray-600">or</span>
                <span className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label htmlFor="username" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    ID
                  </label>
                  <Input
                    ref={passwordIdRef}
                    id="username"
                    type="text"
                    placeholder="your.name"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                    className="dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Password
                  </label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                  />
                </div>
                <Button type="submit" variant="outline" className="w-full mt-2 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              {/* Point at SSO rather than echoing Supabase's generic error,
                  which users read as "I mistyped my password". */}
              {passwordFailed && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-900/50 dark:bg-amber-950/30">
                  <p className="text-sm text-amber-900 dark:text-amber-200">
                    Sign-in failed. Intercomp accounts use Microsoft — try the button above.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Card footer */}
          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <span className="text-xs text-gray-400 dark:text-gray-600">Intercomp</span>
            <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-600">
              <Lock className="h-3 w-3" />
              Secure access
            </span>
          </div>
        </div>

        {/* Page footer */}
        <p className="mt-4 text-center text-xs text-gray-400 dark:text-gray-600">
          AOI Hub · intercomp.com.mt · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
